/**
 * API Error utilities for consistent error handling
 */

/**
 * Extract a user-friendly error message from an API error
 * @param {Error} error - The error from axios
 * @param {string} fallback - Fallback message if extraction fails
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error, fallback = 'Something went wrong') {
  // Handle axios response errors
  if (error?.response?.data) {
    const data = error.response.data;
    
    // FastAPI validation errors
    if (Array.isArray(data) && data[0]?.msg) {
      return data.map(e => e.msg).join('; ');
    }
    
    // Detail string or object
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    if (Array.isArray(data.detail)) {
      return data.detail.map(e => e.msg || e).join('; ');
    }
    
    // Message field
    if (data.message) {
      return data.message;
    }
  }
  
  // Network errors
  if (error?.code === 'ERR_NETWORK') {
    return 'Network error. Please check your connection.';
  }
  
  // Default error message
  if (error?.message) {
    return error.message;
  }
  
  return fallback;
}

/**
 * Get HTTP status code from error
 * @param {Error} error - The error from axios
 * @returns {number|null} HTTP status code or null
 */
export function getErrorStatus(error) {
  return error?.response?.status || null;
}

/**
 * Check if error is a validation error (400)
 * @param {Error} error - The error from axios
 * @returns {boolean}
 */
export function isValidationError(error) {
  return error?.response?.status === 400;
}

/**
 * Check if error is a not found error (404)
 * @param {Error} error - The error from axios
 * @returns {boolean}
 */
export function isNotFoundError(error) {
  return error?.response?.status === 404;
}

/**
 * Check if error is a server error (5xx)
 * @param {Error} error - The error from axios
 * @returns {boolean}
 */
export function isServerError(error) {
  const status = error?.response?.status;
  return status >= 500 && status < 600;
}
